import { Button } from "../../ui/button";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../ui/form";
import { Input } from "../../ui/input";
import { Alert, AlertTitle } from "../../ui/alert";
import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { AzureVectorStoreSettings } from "@/types/VectorStoreSettings";
import { forwardRef, useEffect, useImperativeHandle } from "react";
import { settingsValidator, VectorStoreSettings } from "@shared/validators/admin/settings-validators";

type Props = {
    data?: AzureVectorStoreSettings;
    initialData?: any;
    onDataChange?: (data: any) => void;
    readOnly?: boolean;
    error?: string;
    submitButton?: React.ReactNode;
    onSubmit?: (data: any) => void;
    hideSubmitButton?: boolean;
};

const AzureVectorStoreSettingsForm = forwardRef(
    (
        { data, initialData = {}, onDataChange, submitButton, readOnly, hideSubmitButton, error, onSubmit }: Props,
        ref
    ) => {
        const form = useForm<VectorStoreSettings>({
            resolver: zodResolver(settingsValidator.vectorStoreSettings),
            defaultValues: {
                AZURE_SEARCH_SERVICE_NAME:
                    initialData?.AZURE_SEARCH_SERVICE_NAME || data?.AZURE_SEARCH_SERVICE_NAME || "",
                AZURE_SEARCH_API_KEY: initialData?.AZURE_SEARCH_API_KEY || data?.AZURE_SEARCH_API_KEY || "",
                AZURE_SEARCH_INDEX_NAME: initialData?.AZURE_SEARCH_INDEX_NAME || data?.AZURE_SEARCH_INDEX_NAME || "",
            },
        });

        // Watch for changes and notify parent component
        useEffect(() => {
            const subscription = form.watch((value) => {
                if (onDataChange) {
                    onDataChange(value);
                }
            });

            return () => subscription.unsubscribe();
        }, [form, onDataChange]);

        // Expose methods to parent component
        useImperativeHandle(ref, () => ({
            validateForm: async () => {
                const result = await form.trigger();
                if (!result) {
                    // Get the first error message
                    const errors = form.formState.errors;
                    const firstError = Object.values(errors)[0];
                    if (firstError?.message) {
                        console.error("Azure Vector Store validation error:", firstError.message);
                    }
                }
                return result;
            },
            getFormData: () => {
                return form.getValues();
            },
            submitForm: () => {
                if (onSubmit) {
                    form.handleSubmit(onSubmit)();
                }
            },
        }));

        return (
            <Form {...form}>
                <form onSubmit={form.handleSubmit((data) => onSubmit && onSubmit(data))}>
                    <div>
                        {error && (
                            <Alert variant="destructive" className="mb-2 dark:bg-red-800 dark:text-white">
                                <div className="flex items-center gap-2">
                                    <ExclamationTriangleIcon className="h-4 w-4" />
                                    <AlertTitle className="mb-0">{error}</AlertTitle>
                                </div>
                            </Alert>
                        )}
                        <div className="space-y-3">
                            <FormField
                                control={form.control}
                                name="AZURE_SEARCH_SERVICE_NAME"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel htmlFor={field.name}>Service Name</FormLabel>
                                        <FormControl>
                                            <Input {...field} readOnly={readOnly} />
                                        </FormControl>
                                        <FormMessage {...field} />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="AZURE_SEARCH_INDEX_NAME"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel htmlFor={field.name}>Index Name</FormLabel>
                                        <FormControl>
                                            <Input {...field} readOnly={readOnly} autoComplete="off" autoCorrect="off" />
                                        </FormControl>
                                        <FormMessage {...field} />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="AZURE_SEARCH_API_KEY"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel htmlFor={field.name}>API Key</FormLabel>
                                        <FormControl>
                                            <Input {...field} type="password" readOnly={readOnly} autoComplete="off" autoCorrect="off" />
                                        </FormControl>
                                        <FormMessage {...field} />
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>
                    {submitButton ? (
                        submitButton
                    ) : hideSubmitButton ? null : (
                        <div className="flex justify-end pt-10">
                            <Button type="submit">Save Changes</Button>
                        </div>
                    )}
                </form>
            </Form>
        );
    }
);

export default AzureVectorStoreSettingsForm;
